import { useEffect, useState, type FC } from 'react'
import { DefaultPartNotice } from './default-part-notice.js'
import type { PerformanceParts } from './performance-parts.hook.js'
import { PartPickerSheet } from './part-picker-sheet.js'

export interface PartPromptsProps {
  performanceParts: PerformanceParts
  /** Opens the header part menu. When absent, "Change for this band" opens the picker instead. */
  onOpenPartMenu?: () => void
  /** Positions the default-part notice, e.g. above the view's fixed bottom bars on phones. */
  noticeClassName?: string
}

/**
 * The part prompts shared by both performance views: the picker on a first visit with no parts to
 * use, or the one-time notice when the profile default applies to a band for the first time.
 */
export const PartPrompts: FC<PartPromptsProps> = ({ performanceParts, onOpenPartMenu, noticeClassName }) => {
  const { resolution, chooseParts, dismissPicker, acknowledgeNotice } = performanceParts
  const band = resolution.status === 'resolving' ? null : resolution.band
  const bandId = band?.id ?? null

  /**
   * Acknowledging the notice updates the config, which turns `showDefaultNotice` off at once. Latch the
   * band the notice was raised for, so it stays up until it times out or the song moves to another band.
   */
  const [noticeBandId, setNoticeBandId] = useState<string | null>(null)
  const raiseNotice = resolution.status === 'resolved' && resolution.showDefaultNotice
  useEffect(() => {
    if (raiseNotice && bandId) {
      setNoticeBandId(bandId)
    }
  }, [raiseNotice, bandId])

  /** "Change for this band" without a header menu: the picker, for the band in view. */
  const [changingBandId, setChangingBandId] = useState<string | null>(null)

  const showNotice = resolution.status === 'resolved' && bandId !== null && (raiseNotice || noticeBandId === bandId)
  const asking = resolution.status === 'ask'
  const changing = !asking && bandId !== null && changingBandId === bandId

  const handleChangeForBand = () => {
    if (onOpenPartMenu) {
      onOpenPartMenu()
      return
    }
    setChangingBandId(bandId)
  }

  return (
    <>
      {asking ? (
        <PartPickerSheet
          key={`ask-${bandId ?? 'personal'}`}
          open
          band={resolution.band}
          hasDefault={resolution.hasDefault}
          onChoose={chooseParts}
          onDismiss={dismissPicker}
        />
      ) : null}
      {changing && band ? (
        <PartPickerSheet
          key={`change-${band.id}`}
          open
          band={band}
          hasDefault
          onChoose={(parts, options) => {
            setChangingBandId(null)
            chooseParts(parts, options)
          }}
          onDismiss={() => setChangingBandId(null)}
        />
      ) : null}
      {showNotice && resolution.status === 'resolved' ? (
        <DefaultPartNotice
          key={bandId}
          className={noticeClassName}
          parts={resolution.parts}
          onChangeForBand={handleChangeForBand}
          onShown={acknowledgeNotice}
        />
      ) : null}
    </>
  )
}

export default PartPrompts
